import { METADATA_SHEET_NAME } from "@/utils/metadataSheetSchema";

/**
 * Assemble supports four Excel uploads: Content and Translations in the Editor, Selection and Metadata in the
 * Library. Each one is produced by its own "download template" action and has a distinct layout, so we identify
 * the file from its sheets and header row to stop a file made for one feature from being imported into another.
 */
export const ImportFileType = {
    Content: "content",
    Translations: "translations",
    Selection: "selection",
    Metadata: "metadata"
} as const;

export type ImportFileType = (typeof ImportFileType)[keyof typeof ImportFileType];

const importFileTypeLabel: Record<ImportFileType, string> = {
    [ImportFileType.Content]: "Content",
    [ImportFileType.Translations]: "Translations",
    [ImportFileType.Selection]: "Selection",
    [ImportFileType.Metadata]: "Metadata"
};

const SELECTION_INPUT_SHEET_NAME = "Input";
const TRANSLATIONS_SHEET_NAME = "translations";
const TRANSLATIONS_SOURCE_HEADER = "source";
const CONTENT_VERSION_NAME_HEADER = "version name";

type ImportedWorkbookLike = {
    sheets: string[];
    [sheetName: string]: unknown;
};

const normalize = (value: unknown) => (typeof value === "string" ? value.trim().toLowerCase() : "");

// XlsUploadButton drops the info row, so the first row of sheetData is the header row
function getHeaderRow(importedData: ImportedWorkbookLike, sheetName: string): string[] {
    const sheet = importedData[sheetName] as { sheetData?: unknown[][] } | undefined;
    const header = sheet?.sheetData?.[0] ?? [];

    return header.map(normalize);
}

function findSheetName(importedData: ImportedWorkbookLike, name: string) {
    return importedData.sheets.find(sheetName => normalize(sheetName) === name.toLowerCase());
}

function isMetadataFile(importedData: ImportedWorkbookLike) {
    const sheetName = findSheetName(importedData, METADATA_SHEET_NAME);
    if (!sheetName) {
        return false;
    }

    const header = getHeaderRow(importedData, sheetName);

    return header.includes("size") && header.includes("language");
}

function isSelectionFile(importedData: ImportedWorkbookLike) {
    const sheetName = findSheetName(importedData, SELECTION_INPUT_SHEET_NAME);
    if (!sheetName) {
        return false;
    }

    const [first, second] = getHeaderRow(importedData, sheetName);

    return first === "size" && second === "language";
}

function isTranslationsFile(importedData: ImportedWorkbookLike) {
    const sheetName = findSheetName(importedData, TRANSLATIONS_SHEET_NAME);
    if (!sheetName) {
        return false;
    }

    return getHeaderRow(importedData, sheetName)[0] === TRANSLATIONS_SOURCE_HEADER;
}

function isContentFile(importedData: ImportedWorkbookLike) {
    return importedData.sheets.some(
        sheetName => getHeaderRow(importedData, sheetName)[0] === CONTENT_VERSION_NAME_HEADER
    );
}

export function detectImportFileType(importedData: ImportedWorkbookLike): ImportFileType | null {
    if (isMetadataFile(importedData)) {
        return ImportFileType.Metadata;
    }

    if (isSelectionFile(importedData)) {
        return ImportFileType.Selection;
    }

    if (isTranslationsFile(importedData)) {
        return ImportFileType.Translations;
    }

    if (isContentFile(importedData)) {
        return ImportFileType.Content;
    }

    return null;
}

/**
 * Returns an error message when the uploaded file does not belong to the expected feature, or null when it does.
 *
 * With `strict` the file must be positively recognised as the expected type. Without it, only files recognised as
 * a different feature are rejected, which keeps accepting older files whose layout predates the current templates.
 */
export function getImportFileTypeError(
    importedData: ImportedWorkbookLike,
    expected: ImportFileType,
    { strict = false }: { strict?: boolean } = {}
): string | null {
    const detected = detectImportFileType(importedData);
    const expectedLabel = importFileTypeLabel[expected];

    if (detected === expected) {
        return null;
    }

    if (detected) {
        return `This looks like a ${importFileTypeLabel[detected]} file, which is not supported for ${expectedLabel} import. Please upload a ${expectedLabel} file downloaded from this tab.`;
    }

    if (strict) {
        return `This file is not a valid ${expectedLabel} file. Please download the template from this tab, fill it in and try again.`;
    }

    return null;
}
